import React, { useEffect, useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import AuthService from '../services/auth.service';
import logo from '../assets/logo.png';
import toast from '../utils/toast';

function Login() {
  const navigate = useNavigate();
  const { verifyOTP } = useAuth();
  const isDevMode = process.env.NODE_ENV !== 'production';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [otp, setOtp] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);

  const [devUsers, setDevUsers] = useState([]);
  const [selectedDevEmail, setSelectedDevEmail] = useState('');
  const [devLoading, setDevLoading] = useState(false);
  const [devError, setDevError] = useState('');

  useEffect(() => {
    if (!isDevMode) {
      return;
    }

    const fetchDevUsers = async () => {
      try {
        const response = await AuthService.getDevUsers();
        const usersByRole = response?.usersByRole || {};
        const flattenedUsers = Object.entries(usersByRole).flatMap(([role, users]) =>
          (users || []).map((user) => ({ ...user, role }))
        );

        setDevUsers(flattenedUsers);
        if (flattenedUsers.length > 0) {
          setSelectedDevEmail(flattenedUsers[0].email);
        }
      } catch (fetchError) {
        const status = fetchError?.response?.status || 'network';
        const message = fetchError?.response?.data?.message || fetchError?.message || 'Unknown error';
        setDevError('Unable to load test users (' + status + '): ' + message);
      }
    };

    fetchDevUsers();
  }, [isDevMode]);

  const notify = (message, type) => {
    if (type === 'success') {
      toast.success(message);
    } else if (type === 'error') {
      toast.error(message);
    } else {
      toast(message);
    }
  };

  const handleOtpChange = (e) => {
    setOtp(e.target.value);
  };

  const redirectByRole = (role) => {
    const userRole = String(role || '').toLowerCase();
    if (userRole === 'admin' || userRole === 'manager' || userRole === 'hr') {
      navigate('/management-dashboard', { replace: true });
      return;
    }
    navigate('/dashboard', { replace: true });
  };

  const clearSession = () => {
    localStorage.removeItem('user-info');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('token');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    delete axios.defaults.headers.common.Authorization;

    document.cookie = 'accessToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'refreshToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'checktoken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  };

  const handleDevUserLogin = async () => {
    if (!selectedDevEmail) {
      setDevError('Select a user first.');
      return;
    }

    setDevLoading(true);
    setDevError('');
    setError(null);

    try {
      clearSession();

      const selectedUser = devUsers.find((user) => user.email === selectedDevEmail);
      const response = await AuthService.loginAsDevUser(selectedDevEmail, selectedUser?.role);

      const { accessToken, refreshToken, user } = response || {};
      if (!accessToken || !user) {
        throw new Error('Dev login did not return a valid session');
      }

      const userInfo = {
        user,
        token: accessToken,
        accessToken,
        refreshToken,
      };

      localStorage.setItem('user-info', JSON.stringify(userInfo));
      localStorage.setItem('token', accessToken);
      localStorage.setItem('userEmail', user.email);

      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

      notify(`Test session started for ${user.email}`, 'success');
      setIsRedirecting(true);
      setTimeout(() => {
        redirectByRole(user.role);
      }, 300);
    } catch (loginError) {
      setDevError(loginError.response?.data?.message || loginError.message || 'Dev login failed');
    } finally {
      setDevLoading(false);
    }
  };

  const handleClearDevSession = () => {
    clearSession();
    setIsOtpSent(false);
    setOtp('');
    setUserEmail('');
    setIsRedirecting(false);
    notify('Test session cleared', 'success');
  };

  const verifyOtp = async () => {
    setLoading(true);
    try {
      const result = await verifyOTP(userEmail, otp);

      if (result.success) {
        notify('Welcome back!', 'success');
        setIsRedirecting(true);

        setTimeout(() => {
          const userInfo = JSON.parse(localStorage.getItem('user-info'));
          redirectByRole(userInfo?.user?.role);
        }, 2000);
      } else {
        throw new Error(result.message || 'OTP verification failed');
      }
    } catch (err) {
      notify(err.message || 'OTP verification failed. Please try again.', 'error');
      setError('OTP verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const responseGoogle = async (authResult) => {
    setLoading(true);
    try {
      if (authResult?.code) {
        const result = await AuthService.googleLogin(authResult.code);
        if (result) {
          const email = result.email;
          setUserEmail(email);
          setIsOtpSent(true);
          notify('Google login successful! OTP sent to your email.', 'success');
        } else {
          throw new Error('Invalid token response from server');
        }
      }
    } catch (err) {
      notify('Google login failed. Please try again.', 'error');
      setError('Google login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: responseGoogle,
    onError: (loginError) => {
      console.error('Google login error:', loginError);
      notify('Google login failed. Please try again.', 'error');
      setError('Google login failed. Please try again.');
    },
    flow: 'auth-code',
  });

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="relative w-full max-w-md bg-white/95 backdrop-blur-sm rounded-2xl shadow p-8 space-y-6 border border-white/20">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm text-center">
            {error}
          </div>
        )}

        {loading || isRedirecting ? (
          <div className="flex flex-col justify-center items-center py-12">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-blue-200 rounded-full"></div>
              <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin absolute top-0"></div>
            </div>
            <div className="mt-6 text-center">
              <p className="text-gray-700 font-medium">
                {isRedirecting
                  ? ' Login successful!'
                  : isOtpSent
                    ? ' Verifying OTP...'
                    : ' Authenticating with Google...'}
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {isRedirecting ? 'Redirecting to your dashboard...' : 'Please wait...'}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="text-center">
              <div className="relative mx-auto w-20 h-20 mb-6">
                <div className="absolute inset-0 rounded-2xl shadow"></div>
                <div className="relative w-full h-full bg-white rounded-2xl shadow-inner flex items-center justify-center">
                  <img src={logo} alt="Aerinit IT Services" className="w-12 h-12 rounded-lg" loading="lazy" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Welcome Back</h1>
              <p className="text-gray-600 text-sm">Sign in to your Aerinit IT Services account</p>
            </div>

            {isDevMode && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
                <h2 className="text-sm font-semibold text-amber-900 mb-2">Dev/Test User Session (No Google)</h2>
                <p className="text-xs text-amber-800 mb-3">
                  Select a user and start a full test session. This block is disabled in production.
                </p>

                <div className="space-y-3">
                  <select
                    value={selectedDevEmail}
                    onChange={(e) => setSelectedDevEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm bg-white"
                    disabled={devLoading || devUsers.length === 0}
                  >
                    {devUsers.length === 0 && <option value="">No users available</option>}
                    {devUsers.map((user) => (
                      <option key={user.email} value={user.email}>
                        {user.email}
                      </option>
                    ))}
                  </select>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleDevUserLogin}
                      disabled={devLoading || !selectedDevEmail}
                      className="flex-1 px-3 py-2 text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 text-sm font-medium"
                    >
                      {devLoading ? 'Starting...' : 'Start Test Session'}
                    </button>
                    <button
                      type="button"
                      onClick={handleClearDevSession}
                      className="px-3 py-2 border border-amber-400 text-amber-900 rounded-lg hover:bg-amber-100 text-sm font-medium"
                    >
                      Clear
                    </button>
                  </div>

                  {devError && (
                    <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">{devError}</div>
                  )}
                </div>
              </div>
            )}

            <button
              onClick={googleLogin}
              className="group relative w-full flex items-center justify-center px-6 py-4 bg-white border-2 border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-100"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
              <img
                src="https://developers.google.com/identity/images/g-logo.png"
                alt="Google"
                className="relative w-5 h-5 mr-3"
              />
              <span className="relative text-gray-700 font-medium">Continue with Google</span>
            </button>

            {isOtpSent && (
              <div className="mt-6 space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-sm text-green-700 font-medium">OTP sent successfully!</span>
                  </div>
                  <p className="text-xs text-green-600 mt-1">Check your email: {userEmail}</p>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Enter OTP from your email:</label>
                  <input
                    type="text"
                    value={otp}
                    onChange={handleOtpChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-lg font-mono"
                    placeholder="000000"
                    maxLength="6"
                    autoFocus
                  />
                </div>

                <button
                  onClick={verifyOtp}
                  disabled={!otp || otp.length < 6}
                  className="w-full px-4 py-3 text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  Verify OTP & Login
                </button>
              </div>
            )}

            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Having trouble? <a href="/help" className="text-blue-500 hover:underline">Help & Guide</a>
              </p>
            </div>
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-500">Aerinit IT Services, Rajiv Gandhi Technology Park, Chandigarh</p>
              <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} All Rights Reserved</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Login;



